type PDPQuantities = Record<string, number>;
type PDPSizes = Record<string, { size: string; quantity: number }>;

type Variant = {
  id: string;
  size: string;
};

/**
 * Maps PDP quantities and sizes to Design Lab variant format
 *
 * @param pdpQuantities - Quantities from PDP form (variantId -> quantity)
 * @param pdpSizes - Size data from PDP form (variantId -> {size, quantity})
 * @param designLabVariants - Variants available in Design Lab
 * @returns Object mapping Design Lab variant IDs to quantities
 */
export function mapPdpQuantitiesToDesignLab(
  pdpQuantities: PDPQuantities,
  pdpSizes: PDPSizes,
  designLabVariants: Variant[]
): Record<string, number> {
  const result: Record<string, number> = {};

  if (!pdpQuantities || !pdpSizes || !designLabVariants) {
    return result;
  }

  // Create a size -> quantity mapping from PDP data
  const sizeQuantityMap: Record<string, number> = {};

  Object.entries(pdpQuantities).forEach(([variantId, quantity]) => {
    const sizeData = pdpSizes[variantId];
    if (sizeData && quantity > 0) {
      sizeQuantityMap[sizeData.size] = (sizeQuantityMap[sizeData.size] || 0) + quantity;
    }
  });

  // Map to Design Lab variants by matching sizes
  designLabVariants.forEach(variant => {
    const pdpQuantity = sizeQuantityMap[variant.size];
    if (pdpQuantity && pdpQuantity > 0) {
      result[variant.id] = pdpQuantity;
    }
  });

  return result;
}

/**
 * Checks if PDP quantities are valid and non-empty
 */
export function hasPdpQuantities(pdpQuantities?: PDPQuantities, pdpSizes?: PDPSizes): boolean {
  return !!(
    pdpQuantities &&
    pdpSizes &&
    Object.keys(pdpQuantities).length > 0 &&
    Object.keys(pdpSizes).length > 0
  );
}
